import React, { Component } from 'react';
import PropTypes from 'prop-types';
import map from 'lodash/map';
import flow from 'lodash/fp/flow';
import _map from 'lodash/fp/map';
import _sortBy from 'lodash/fp/sortBy';
import reduce from 'lodash/reduce';
import debounce from 'lodash/debounce';
import some from 'lodash/some';
import Rate from 'antd/lib/rate';
import Slider from 'antd/lib/slider';
import Switch from 'antd/lib/switch';
import Checkbox from 'antd/lib/checkbox';
import Spin from 'antd/lib/spin';
import Input from 'antd/lib/input';
import { Pagination } from 'semantic-ui-react';
import { ToggleButtonTooltip } from './ToggleButtonTooltip';
import { Button } from '../Button';
import PinIcon from './pin.svg';
import EmptyPicIcon from './empty-pic.svg';
import FinalCutProIcon from './final-cut-pro.svg';
import AdobePremiereIcon from './adobe-premiere.svg';
import AfterEffectsIcon from './after-effects.svg';
import AppleMotionIcon from './apple-motion.svg';
import DavinciResolveIcon from './davinci-resolve.svg';
import { getFirstThreeElements } from '../../shared/helpers';
import './FreelancerChooser.css';

const CheckboxGroup = Checkbox.Group;

export class FreelancerChooser extends Component {
  constructor(props) {
    super(props);

    this.state = {
      openToggleButtonTooltip: true,
      showOtherLanguageInput: false,
      instantBook: false,
      activePage: 1,
      numberOfProjects: 0,
      availabilities: [],
      preProductionSkills: [],
      animationStyles: [],
      softwarePreferences: [],
      fluentLanguages: [],
      fluentLanguageOtherInputValue: '',
    };

    this.debouncedHandleOtherLanguageInputChange = debounce(
      this.debouncedHandleOtherLanguageInputChange,
      500,
    );

    this.handleNumberOfProjectsChange = debounce(
      this.handleNumberOfProjectsChange,
      400,
    );
  }

  componentDidMount() {
    const { handleShowChooserPopup } = this.props;
    handleShowChooserPopup();
  }

  updateFilterState = state =>
    this.setState(
      {
        activePage: 1,
        ...state,
      },
      this.fetchFreelancers,
    );

  fetchFreelancers = () => {
    const {
      activePage,
      instantBook,
      numberOfProjects,
      availabilities,
      preProductionSkills,
      animationStyles,
      softwarePreferences,
      fluentLanguages,
      fluentLanguageOtherInputValue,
    } = this.state;

    const mappedAvailabilities = reduce(
      availabilities,
      (acc, availability) => ({
        ...acc,
        [`q[${availability}]`]: 1,
      }),
      {},
    );

    this.props.handleFetchFreelancers({
      page: activePage,
      ...mappedAvailabilities,
      'q[instant_book_eq]': instantBook || undefined,
      'q[min_projects]': numberOfProjects || undefined,
      'q[skills_cont_all]': [
        ...preProductionSkills,
        ...animationStyles,
        ...softwarePreferences,
      ],
      'q[fluent_languages_cont_all]': fluentLanguageOtherInputValue
        ? [...fluentLanguages, fluentLanguageOtherInputValue]
        : fluentLanguages,
    });
  };

  handleNumberOfProjectsChange = numberOfProjects =>
    this.updateFilterState({ numberOfProjects });

  handleCloseToggleButtonTooltip = () =>
    this.setState(({ openToggleButtonTooltip }) => ({
      openToggleButtonTooltip: !openToggleButtonTooltip,
    }));

  handleInstantBookChange = instantBook =>
    this.updateFilterState({
      instantBook,
      openToggleButtonTooltip: false,
    });

  handleAvailabilityChange = availabilities =>
    this.updateFilterState({ availabilities });

  handlePreProductionSkillsChange = preProductionSkills =>
    this.updateFilterState({ preProductionSkills });

  handleAnimationStylesChange = animationStyles =>
    this.updateFilterState({ animationStyles });

  handleSoftwarePreferencesChange = softwarePreferences =>
    this.updateFilterState({ softwarePreferences });

  handleFluentLanguagesChange = fluentLanguages =>
    this.updateFilterState({ fluentLanguages });

  handleOtherLanguageCheckboxChange = () =>
    this.setState(({ showOtherLanguageInput }) => ({
      showOtherLanguageInput: !showOtherLanguageInput,
    }));

  handleOtherLanguageInputChange = ({ target: { value } }) =>
    this.debouncedHandleOtherLanguageInputChange(value);

  debouncedHandleOtherLanguageInputChange = fluentLanguageOtherInputValue =>
    this.updateFilterState({ fluentLanguageOtherInputValue });

  handlePageChange = (e, { activePage }) =>
    this.updateFilterState({ activePage });

  render() {
    const {
      projectId,
      freelancers,
      pagination,
      handleBookNow,
      handleRequestABid,
      handleDeleteABid,
      handleFreelancerCardClicked,
      isFetchingFreelancers,
    } = this.props;

    const freelancerBided = flow(
      _map(freelancer => {
        const bidRequested = some(freelancer.bids, {
          project_id: projectId,
        });
        return { ...freelancer, bidRequested };
      }),
      _sortBy(freelancer => !freelancer.bidRequested),
    )(freelancers);

    return (
      <div className="freelancer-chooser">
        <div className="freelancer-chooser__filters">
          <div className="freelancer-chooser__filters-header">Filters</div>
          <div className="freelancer-chooser__filters-content">
            <div className="freelancer-chooser__filters-label">
              Projects In Portfolio
            </div>
            <Slider
              min={0}
              max={10}
              defaultValue={0}
              onChange={this.handleNumberOfProjectsChange}
            />

            <div className="freelancer-chooser__filters-label">
              Instant Book
            </div>
            <div className="freelancer-chooser__filters-tooltip-container">
              <ToggleButtonTooltip
                open={this.state.openToggleButtonTooltip}
                handleCloseTooltip={this.handleCloseToggleButtonTooltip}
              />
              <Switch
                onChange={this.handleInstantBookChange}
                className="freelancer-chooser__filters-toggle"
              />
            </div>

            <div className="freelancer-chooser__filters-label">
              Availability
            </div>
            <CheckboxGroup
              className="freelancer-chooser__filters-checkbox-group"
              onChange={this.handleAvailabilityChange}
            >
              <Checkbox value="full_time">Full Time</Checkbox>
              <Checkbox value="part_time">Part Time</Checkbox>
            </CheckboxGroup>

            <div className="freelancer-chooser__filters-label">
              Pre-Production Skills
            </div>
            <CheckboxGroup
              className="freelancer-chooser__filters-checkbox-group"
              onChange={this.handlePreProductionSkillsChange}
            >
              <Checkbox value="text_script">Creating script</Checkbox>
              <Checkbox value="music">Selecting music</Checkbox>
              <Checkbox value="voiceover">Selecting a voiceover actor</Checkbox>
              <Checkbox value="stock_footage">Selecting stock footage</Checkbox>
              <Checkbox value="storyboard">Creating storyboard</Checkbox>
              <Checkbox value="shot_list">Creating shot list</Checkbox>
            </CheckboxGroup>

            <div className="freelancer-chooser__filters-label">
              Animation Styles
            </div>
            <CheckboxGroup
              className="freelancer-chooser__filters-checkbox-group"
              onChange={this.handleAnimationStylesChange}
            >
              <Checkbox value="chart_based">Character based</Checkbox>
              <Checkbox value="line_art">Line art</Checkbox>
              <Checkbox value="infographic">Infographic</Checkbox>
              <Checkbox value="3d_animation">3D Animation</Checkbox>
              <Checkbox value="whiteboard">Whiteboard</Checkbox>
              <Checkbox value="kinetic_text">Kinetic text</Checkbox>
              <Checkbox value="2d_vector">2D vector</Checkbox>
              <Checkbox value="motion_tracking">Stop motion</Checkbox>
            </CheckboxGroup>

            <div className="freelancer-chooser__filters-label">
              Software Preferences
            </div>
            <CheckboxGroup
              className="freelancer-chooser__filters-checkbox-group-icon"
              onChange={this.handleSoftwarePreferencesChange}
            >
              <Checkbox value="final_cut">Final Cut Pro</Checkbox>
              <img alt="final cut pro" src={FinalCutProIcon} />
              <Checkbox value="adobe_premiere">Adobe Premiere</Checkbox>
              <img alt="adobe premiere" src={AdobePremiereIcon} />
              <Checkbox value="after_effects">After Effects</Checkbox>
              <img alt="after effects" src={AfterEffectsIcon} />
              <Checkbox value="apple_motion">Apple Motion</Checkbox>
              <img alt="apple motion" src={AppleMotionIcon} />
              <Checkbox value="davinci_resolve">Davinci Resolve</Checkbox>
              <img alt="davinci resolve" src={DavinciResolveIcon} />
            </CheckboxGroup>

            <div className="freelancer-chooser__filters-label">
              Fluent Languages
            </div>
            <CheckboxGroup
              className="freelancer-chooser__filters-checkbox-group"
              onChange={this.handleFluentLanguagesChange}
            >
              <Checkbox value="English">English</Checkbox>
              <Checkbox value="Spanish">Spanish</Checkbox>
              <Checkbox value="Portuguese">Portuguese</Checkbox>
              <Checkbox value="German">German</Checkbox>
              <Checkbox value="Hebrew">Hebrew</Checkbox>
              <Checkbox value="Italian">Italian</Checkbox>
            </CheckboxGroup>

            <div className="freelancer-chooser__filters-other-language-checkbox">
              <Checkbox
                value="other"
                onChange={this.handleOtherLanguageCheckboxChange}
              >
                Other
              </Checkbox>
              {this.state.showOtherLanguageInput && (
                <Input
                  onChange={this.handleOtherLanguageInputChange}
                  className="freelancer-chooser__filters-other-language-input"
                  placeholder="type..."
                />
              )}
            </div>
          </div>
        </div>
        <div className="freelancer-chooser__freelancers-container">
          {pagination && (
            <Pagination
              className="freelancer-chooser__freelancers-pagination"
              activePage={pagination.current_page}
              totalPages={pagination.total_pages}
              onPageChange={this.handlePageChange}
            />
          )}
          <Spin spinning={isFetchingFreelancers} size="large">
            <div className="freelancer-chooser__freelancers-list">
              {map(freelancerBided, freelancer => {
                const { bidRequested } = freelancer;
                const bidAction = () => {
                  if (!bidRequested) {
                    return handleRequestABid(freelancer, this.fetchFreelancers);
                  }
                  return handleDeleteABid(freelancer, this.fetchFreelancers);
                };

                return (
                  <div
                    key={freelancer.id}
                    className="freelancer-chooser__freelancers-card"
                  >
                    <div
                      className="freelancer-chooser__freelancers-profile-click-area"
                      onClick={() =>
                        handleFreelancerCardClicked(
                          freelancer,
                          this.fetchFreelancers,
                        )
                      }
                    >
                      {freelancer.instant_book && (
                        <div>
                          <div className="freelancer-chooser__freelancers-instant-book-tag">
                            Instant book
                          </div>
                          <div className="freelancer-chooser__freelancers-instant-book-price">
                            $2,930
                          </div>
                        </div>
                      )}
                      <div className="freelancer-chooser__freelancers-picture-container">
                        <img
                          className="freelancer-chooser__freelancers-picture"
                          alt="freelancer"
                          src={
                            freelancer.avatar_url
                              ? freelancer.avatar_url
                              : EmptyPicIcon
                          }
                        />
                      </div>
                      <div className="freelancer-chooser__freelancers-name">
                        {freelancer.name}
                      </div>
                      {freelancer.state && (
                        <div className="freelancer-chooser__freelancers-state">
                          <img alt="map pin" src={PinIcon} />
                          <span> {freelancer.state}</span>
                        </div>
                      )}
                      <div className="freelancer-chooser__freelancers-rate">
                        <Rate allowHalf disabled defaultValue={5} />
                      </div>
                      <div className="freelancer-chooser__freelancers-response-rate">
                        60% Response Rate | 6 Projects
                      </div>
                      <div className="freelancer-chooser__freelancers-bio">
                        {freelancer.bio}
                      </div>
                      <div className="freelancer-chooser__freelancers-skills">
                        {map(
                          getFirstThreeElements(freelancer.formatted_skills),
                          (skill, index) => (
                            <span key={String(index)}>{skill}</span>
                          ),
                        )}
                      </div>
                    </div>
                    <div className="freelancer-chooser__freelancers-actions">
                      {freelancer.instant_book && (
                        <Button
                          flat
                          rounded
                          className="freelancer-chooser__freelancers-actions--medium"
                          title="Book now"
                          onClick={() => handleBookNow(freelancer)}
                        />
                      )}

                      <Button
                        flat
                        rounded
                        bordered={!bidRequested}
                        toggle={bidRequested}
                        className={`freelancer-chooser__freelancers-actions--${
                          freelancer.instant_book ? 'medium' : 'large'
                        }`}
                        textClassName={
                          bidRequested
                            ? 'freelancer-chooser__freelancers-actions--disabled'
                            : ''
                        }
                        title={bidRequested ? 'Requested' : 'Request a bid'}
                        onClick={bidAction}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Spin>
        </div>
      </div>
    );
  }
}

FreelancerChooser.propTypes = {
  projectId: PropTypes.number,
  freelancers: PropTypes.array,
  pagination: PropTypes.object,
  handleFetchFreelancers: PropTypes.func.isRequired,
  handleBookNow: PropTypes.func.isRequired,
  handleRequestABid: PropTypes.func.isRequired,
  handleDeleteABid: PropTypes.func.isRequired,
  handleShowChooserPopup: PropTypes.func.isRequired,
  handleFreelancerCardClicked: PropTypes.func.isRequired,
  isFetchingFreelancers: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/components/FreelancerChooser/FreelancerChooser.js